import React from 'react';
import { CommonStyles } from '~common/react/CommonStyles';
import { ReactActionDialogBodyProps } from '~commonInfra/dialogs/ReactActionDialog';

import { RoseTree } from '@tableau/data-structures';
import {
    DestinationProjectStatus, NestedItemBasicInfo, NestedItemsTreeHelper,
    NestedProjectExplorer
} from '@tableau/nested-project-explorer';
import { NotificationsCoreFormatters } from '@tableau/user-notifications-client-core';
import { ActivitySpinnerWidget, TabStyles } from '@tableau/widgets';

import {
    DestinationProjectContentType, DestinationProjects
} from '../../projects/resources/DestinationProjects';
import {
    getSessionManagementServiceInstance
} from '../../sessionManagement/SessionManagementService';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { L10n } from '../../ts/L10n/L10n';
import { DestinationProjectMapper } from '../../ts/mappers/DestinationProjectMapper';
import { IContentType, ProjectContentType } from '../../ts/models/ContentTypes';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { Set } from '../../ts/models/Set';
import { ITranslationIds } from './MoveToProjectAction';
import { projectExplorerStyles } from '~src/common/react/components/ProjectExplorerStyles';

/** STYLES */
const activitySpinnerStyles: React.CSSProperties = {
  textAlign: 'center', marginTop: TabStyles.Spacing.ExtraLargeSpace
};
const messageStyles: React.CSSProperties = { marginBottom: TabStyles.Spacing.LargeSpace, marginTop: '0px' };

/** TEST IDS */
export const ACTIVITY_SPINNER_TEST_ID = 'nested-project-explorer-waiting';
export const MESSAGE_TEST_ID = 'dialog-message';

export type MoveToProjectDialogBodyState = {
  selectedProject: NestedItemBasicInfo;
}

export type MoveToProjectDialogBodyProps = {
  items: Set<INamedItemInfo>;
  itemType: IContentType;
  moveFn: (projectId: string, itemIds: string[]) => Promise<any>;
  translations: ITranslationIds;
}

export type MoveToProjectActionDialogProps = ReactActionDialogBodyProps<MoveToProjectDialogBodyProps, MoveToProjectDialogBodyState>;

export function MoveToProjectDialogBody(props: MoveToProjectActionDialogProps) {
  const [projects, setProjects] = React.useState(undefined);

  React.useEffect(() => {
    let isMounted = true;
    MoveToProjectDialog.getAllProjectsAsTrees(props.itemType, props.items.map(item => item.id))
      .then(projectTrees => {
        if (isMounted) setProjects(projectTrees);
      });
    return () => isMounted = false;
  },[]);

  return <>
    <p data-tb-test-id={MESSAGE_TEST_ID} style={messageStyles}>
      {props.translations.promptId({
        ITEM_COUNT: props.items.size(),
        ITEM_COUNT_LOC: NotificationsCoreFormatters.formatCount(props.items.size()),
        ITEM_NAME: L10n.elideIfLong(props.items.first().name)
      })}
    </p>
    <div style={projectExplorerStyles}>
      {projects &&
        <NestedProjectExplorer
          topLevelDestinationStatus={getTopLevelDestinationStatus(props.itemType)}
          projects={projects}
          onSelectedProjectChanged={
            (project: NestedItemBasicInfo | null) => props.updateDialogState({selectedProject: project})
          }/>
      }
      {!projects &&
        <div style={activitySpinnerStyles}>
          <ActivitySpinnerWidget testId={ACTIVITY_SPINNER_TEST_ID} />
        </div>
      }
    </div>
  </>;
}

  // This method is separate so it can be mocked in unit tests
export function getAllProjectsAsTrees(
  itemType: IContentType,
  itemIds: string[],
): Promise<RoseTree<NestedItemBasicInfo>[]> {
  const contentType = itemType.name as DestinationProjectContentType;
  const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(contentType, itemIds, '');
  return SliceableHelper.fetchAll(projectsQuery).then(projects => {
    const destinationProjects = DestinationProjectMapper.map(projects.items);
    return NestedItemsTreeHelper.convertItemListToTrees(destinationProjects);
  });
}

function getTopLevelDestinationStatus(itemType: IContentType): DestinationProjectStatus {
  if (itemType !== ProjectContentType) {
    return 'structurally_invalid';
  } else if (!getSessionManagementServiceInstance().isSessionSiteOrServerAdmin()) {
    return 'insufficient_permissions';
  } else {
    return 'valid';
  }
}

// Export for stubbing in tests
export const MoveToProjectDialog = {
  getAllProjectsAsTrees
};
